import React, { Component } from "react"
import { defineMessages, FormattedMessage, injectIntl } from "react-intl"

import moment from "moment-timezone"
import Icon from "../../common/icon/icon.component.jsx"
import CalendarEventDatePickers from "../calendareventdatepickers.component.jsx"
import DeprecatedUserSelect from "../../common/user/selection.component.jsx"
import CustomerSelect from "../../customer/selection.component.jsx"
import ActionButton from "../../common/button/actionbutton.component.jsx"
import ConfirmButton from "../../common/button/confirmbutton.component.jsx"
import { getEventName } from "../../name-helpers/eventNameHelper"
import { capitalize } from "../../name-helpers/nameFormatter"
import CalendarEventService from "../../state/calendar-event/CalendarEventService"

const messages = defineMessages({
    event_title_placeholder: {
        id: "calendar.new_event.title_placeholder",
        defaultMessage: "Enter the title of your {eventName}"
    },
    event_location_placeholder: {
        id: "calendar.new_event.location_placeholder",
        defaultMessage: "Enter the location of your {eventName}"
    },
    event_description_placeholder: {
        id: "calendar.new_event.desc_placeholder",
        defaultMessage: "Enter the description of your {eventName} (optional)"
    },
    event_internal_notes_placeholder: {
        id: "calendar.new_event.internal_notes_placeholder",
        defaultMessage: "Internal notes about this {eventName} (optional)"
    }
})

class CalendarEventEditForm extends Component {
    constructor(props, context) {
        super(props, context)

        const now = moment()
        const remainder = 30 - (now.minute() % 30)
        const startAt = now.add(remainder, "m").toDate()

        this.state = {
            className: this.props.className || "",
            calendarEventId: this.props.calendarEventId || "",
            selectedCalendarId: this.props.selectedCalendarId || "",
            selectedCalendarLabel: this.props.selectedCalendarLabel || "",
            startAt: this.props.startAt || startAt,
            endAt:
                this.props.endAt ||
                moment(startAt)
                    .add(30, "m")
                    .toDate(),
            timezone: this.props.timezone || moment.tz.guess(),

            contactId: this.props.contactId || "",
            userId: this.props.userId || "",
            eventTitle: this.props.eventTitle || "",
            eventLocation: this.props.eventLocation || "",
            eventDescription: this.props.eventDescription || "",
            internalNotes: "",

            invoiceId: this.props.invoiceId || "",
            quoteId: this.props.quoteId || "",
            orderId: this.props.orderId || "",
            //projectId of invoice/quote/order reference
            projectId: this.props.projectId || "",
            identifier: this.props.identifier || "",
            proposal: this.props.proposal,

            isDeleting: false,
            isSaving: false
        }

        this.handleFormChange = this.handleFormChange.bind(this)
        this.saveCalendarEvent = this.saveCalendarEvent.bind(this)
        this.deleteCalendarEvent = this.deleteCalendarEvent.bind(this)
        this.onCalendarSelectionChange = this.onCalendarSelectionChange.bind(this)
        this.onStartAtChange = this.onStartAtChange.bind(this)
        this.onEndAtChange = this.onEndAtChange.bind(this)
    }

    componentDidMount() {
        if (this.state.calendarEventId) {
            CalendarEventService.get(this.state.calendarEventId).then(event => {
                if (!event.isError) {
                    this.setState({
                        selectedCalendarId: event.getCalendarId(),
                        selectedCalendarLabel: event.getCalendarLabel(),
                        eventTitle: event.getTitle(),
                        eventLocation: event.getLocation(),
                        eventDescription: event.getDescription(),
                        startAt: moment(event.getStartAt()).toDate(),
                        endAt: moment(event.getEndAt()).toDate(),
                        invoiceId: event.getInvoiceId(),
                        quoteId: event.getQuoteId(),
                        orderId: event.getOrderId(),
                        contactId: event.getContactId(),
                        userId: event.getUserId(),
                        internalNotes: event.getLastInternalNote()
                    })
                }
            })
        }
    }

    handleFormChange(event) {
        const target = event.target
        this.setState({
            [target.name]: target.value
        })
    }

    saveCalendarEvent(e) {
        e.preventDefault()
        this.setState({
            isSaving: true
        })
        const payload = {
            calendarId: this.state.selectedCalendarId,
            eventTitle: this.state.eventTitle,
            eventLocation: this.state.eventLocation,
            eventDescription: this.state.eventDescription,
            startAt: moment(this.state.startAt).toISOString(),
            endAt: moment(this.state.endAt).toISOString(),
            timezone: this.state.timezone,
            invoiceId: this.state.invoiceId,
            quoteId: this.state.quoteId,
            orderId: this.state.orderId,
            contactId: this.state.contactId,
            userId: this.state.userId,
            projectId: this.state.projectId,
            internalNotes: this.state.internalNotes,
            identifier: this.state.identifier,
            proposalLink: this.state.proposal ? this.state.proposal.attributes.publicLink : ""
        }
        if (!this.state.calendarEventId) {
            CalendarEventService.create(this.state.selectedCalendarId, payload).then(event => {
                if (!event.isError && this.props.onSave) {
                    this.triggerOnSave(event.getId())
                }
            })
        } else {
            CalendarEventService.update(this.state.calendarEventId, payload).then(event => {
                if (!event.isError && this.props.onSave) {
                    this.triggerOnSave(event.getId())
                }
            })
        }
    }

    deleteCalendarEvent(e) {
        e.preventDefault()
        this.setState({
            isDeleting: true
        })
        CalendarEventService.delete(this.state.calendarEventId).then(event => {
            if (!event.isError && this.props.onSave) {
                this.triggerOnSave()
            }
        })
    }

    triggerOnSave(calendarEventId) {
        setTimeout(() => {
            this.setState(
                {
                    isDeleting: false,
                    isSaving: false
                },
                () => {
                    this.props.onSave(calendarEventId)
                }
            )
        }, 200)
    }

    canCreateEvent() {
        return (
            this.state.selectedCalendarId &&
            this.state.eventTitle &&
            this.state.eventLocation &&
            this.state.startAt &&
            this.state.endAt && 
            this.state.contactId &&
            this.state.userId
        )
    }

    isSameDay() {
        return (
            this.state.startAt.getFullYear() === this.state.endAt.getFullYear() &&
            this.state.startAt.getMonth() === this.state.endAt.getMonth() &&
            this.state.startAt.getDay() === this.state.endAt.getDay()
        )
    }

    onCalendarSelectionChange(selectedCalendarId, selectedCalendarLabel) {
        this.setState({
            selectedCalendarId,
            selectedCalendarLabel
        })
    }

    onStartAtChange(date) {
        const momentDate = moment(date)
        this.setState(
            {
                startAt: date,
                endAt: momentDate.isBefore(moment(this.state.endAt)) ? this.state.endAt : momentDate.add(30, "m").toDate()
            },
            () => {
                if (this.props.onDateChange) {
                    this.props.onDateChange(this.state.startAt, this.state.endAt)
                }
            }
        )
    }

    onEndAtChange(date) {
        this.setState(
            {
                endAt: date
            },
            () => {
                if (this.props.onDateChange) {
                    this.props.onDateChange(this.state.startAt, this.state.endAt)
                }
            }
        )
    }

    render() {
        return (
            <div>
                <CalendarEventDatePickers
                    startAt={this.state.startAt}
                    endAt={this.state.endAt}
                    selectedCalendarId={this.state.selectedCalendarId}
                    selectedCalendarLabel={this.state.selectedCalendarLabel}
                    timezone={this.state.timezone}
                    onDateChange={(startAt, endAt) => {
                        this.onStartAtChange(startAt)
                        this.onEndAtChange(endAt)
                    }}
                    onCalendarChange={(selectedCalendarId, selectedCalendarLabel, selectedCalendar) => {
                        this.onCalendarSelectionChange(selectedCalendarId, selectedCalendarLabel)
                        if (this.props.onCalendarChange) {
                            this.props.onCalendarChange(selectedCalendarId, selectedCalendarLabel, selectedCalendar)
                        }
                    }}
                    calendarSelectDisabled={this.state.calendarEventId ? true : false}
                    calendarClassName={"col-12 mb-3 d-flex align-items-center"}
                    datePickerWrapperClassName={"col-12 mb-3 d-flex align-items-center"}
                    datePickerClassName={"flex-column"}
                />
                <div className={"form-row "}>
                    <div className="col-md-6 mb-3">
                        <label htmlFor="contactId">
                            <FormattedMessage id="calendar.new_event.contact_label" defaultMessage="Contact" />
                        </label>
                        <span className="text-danger">*</span>
                        <CustomerSelect
                            value={this.state.contactId}
                            onChange={contactId => {
                                this.setState({
                                    contactId: contactId
                                })
                                if (this.props.onContactChange) {
                                    this.props.onContactChange(contactId)
                                }
                            }}
                            clearable={false}
                            allowCreate={false}
                        />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label htmlFor="userId">
                            <FormattedMessage id="calendar.new_event.user_label" defaultMessage="Assigned to" />
                        </label>
                        <span className="text-danger">*</span>
                        <DeprecatedUserSelect
                            multi={false}
                            value={this.state.userId}
                            onChange={userId => {
                                this.setState({
                                    userId: userId
                                })
                            }}
                            clearable={false}
                        />
                    </div>
                </div>

                <div className={"form-row "}>
                    <div className="col-md-12 mb-3">
                        <label htmlFor="eventTitle">
                            <FormattedMessage
                                id="calendar.new_event.title_label"
                                defaultMessage="{eventName} title"
                                values={{ eventName: capitalize(getEventName(this.props.intl)) }}
                            />
                        </label>
                        <span className="text-danger">*</span>
                        <input
                            type="text"
                            className="form-control"
                            id="eventTitle"
                            name="eventTitle"
                            value={this.state.eventTitle}
                            placeholder={this.props.intl.formatMessage(messages["event_title_placeholder"], {
                                eventName: getEventName(this.props.intl)
                            })}
                            onChange={this.handleFormChange}
                        />
                    </div>
                </div>

                <div className={"form-row "}>
                    <div className="col-md-12 mb-3">
                        <label htmlFor="eventLocation">
                            <FormattedMessage
                                id="calendar.new_event.location_label"
                                defaultMessage="{eventName} location"
                                values={{ eventName: capitalize(getEventName(this.props.intl)) }}
                            />
                        </label>
                        <span className="text-danger">*</span>
                        <input
                            type="text"
                            className="form-control"
                            id="eventLocation"
                            name="eventLocation"
                            value={this.state.eventLocation}
                            placeholder={this.props.intl.formatMessage(messages["event_location_placeholder"], {
                                eventName: getEventName(this.props.intl)
                            })}
                            onChange={this.handleFormChange}
                        />
                    </div>
                </div>

                <div className={"form-row "}>
                    <div className="col-md-12 mb-3">
                        <label htmlFor="eventDescription">
                            <FormattedMessage
                                id="calendar.new_event.event_desc_label"
                                defaultMessage="{eventName} description"
                                values={{ eventName: capitalize(getEventName(this.props.intl)) }}
                            />
                        </label>
                        <textarea
                            rows="3"
                            className="form-control"
                            id="eventDescription"
                            name="eventDescription"
                            value={this.state.eventDescription}
                            placeholder={this.props.intl.formatMessage(messages["event_description_placeholder"], {
                                eventName: getEventName(this.props.intl)
                            })}
                            onChange={this.handleFormChange}
                        />
                    </div>
                </div>

                <div className={"form-row "}>
                    <div className="col-md-12 mb-3">
                        <label htmlFor="internalNotes">
                            <FormattedMessage
                                id="calendar.new_event.event_internal_notes_label"
                                defaultMessage="Internal Notes"
                            />
                        </label>
                        <textarea
                            rows="3"
                            className="form-control"
                            id="internalNotes"
                            name="internalNotes"
                            value={this.state.internalNotes}
                            placeholder={this.props.intl.formatMessage(messages["event_internal_notes_placeholder"], {
                                eventName: getEventName(this.props.intl)
                            })}
                            onChange={this.handleFormChange}
                        />
                    </div>
                </div>

                <div className={"d-flex w-100 justify-content-between"}>
                    <div className="d-flex justify-content-start">
                        {this.state.calendarEventId ? (
                            <ActionButton
                                type="button"
                                onClick={this.deleteCalendarEvent}
                                className="btn btn-danger"
                                isLoading={this.state.isDeleting}
                                style={{ height: "40px" }}
                                disabled={this.state.isSaving}
                            >
                                <Icon value={"trash"} className={""} />
                            </ActionButton>
                        ) : (
                            ""
                        )}
                    </div>

                    <div className="d-flex justify-content-end">
                        {this.props.footerModalButton}
                        <ActionButton
                            type="submit"
                            className="btn btn-primary"
                            onClick={this.saveCalendarEvent}
                            disabled={!this.canCreateEvent() || this.state.isDeleting}
                            isLoading={this.state.isSaving}
                            style={{ height: "40px" }}
                        >
                            <Icon value={"save"} className={""} />
                            {this.props.saveButtonLabel}
                        </ActionButton>
                    </div>
                </div>
            </div>
        )
    }
}

export default injectIntl(CalendarEventEditForm)



// WEBPACK FOOTER //
// ./src/calendar/components/CalendarEventEditFormComponent.jsx